import { Component, OnInit } from '@angular/core';
import { WebsiteService } from '../../services/website.service';
import { AuthService } from '../../services/auth.service';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Observable } from 'rxjs';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { Firestore, doc, getDoc, setDoc } from '@angular/fire/firestore';
import { User } from '@angular/fire/auth';
import { NotificationService } from '../../services/notification.service';
import { NotificationComponent } from '../../components/notification/notification.component';
import { Storage, ref, uploadBytes, getDownloadURL, deleteObject } from '@angular/fire/storage';
import { HttpClient } from '@angular/common/http';
import { ImageUploadService } from '../../services/image-upload.service';

interface NavigationTheming {
  menuPosition: 'left' | 'center' | 'right';
  logoPosition: 'left' | 'center' | 'right' | 'separate';
  backgroundColor: string;
  borderEnabled: boolean;
  borderColor: string;
  borderWidth: number;
  borderStyle: 'solid' | 'dashed' | 'dotted';
  marginLeft: number;
  marginRight: number;
  padding: number;
  height: number;
}

interface HeadingStyles {
  fontSize: number;
  fontWeight: string;
  fontStyle: 'normal' | 'italic';
  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  letterSpacing: number;
  textDecoration: 'none' | 'underline' | 'line-through';
  lineHeight: number;
  color: string;
}

interface GlobalTheming {
  fontFamily: string;
  backgroundColor: string;
  textColor: string;
  linkColor: string;
  linkHoverColor: string;
  bodyFontSize: number;
  lineHeight: number;
  headings: {
    h1?: HeadingStyles;
    h2?: HeadingStyles;
    h3?: HeadingStyles;
    h4?: HeadingStyles;
    h5?: HeadingStyles;
    h6?: HeadingStyles;
  };
}

interface WebsiteSettings {
  domain: string;
  languages: string[];
  defaultLanguage: string;
  logo?: string;
  seo: {
    headerTags: string;
    bodyTags: string;
    footerScripts: string;
    googleAnalyticsId?: string;
  };
  theming?: {
    navigation?: NavigationTheming;
    global?: GlobalTheming;
  };
}

interface FooterTheming {
  backgroundColor: string;
  textColor: string;
  padding: number;
  linkColor?: string;
  linkHoverColor?: string;
  borderColor?: string;
}

interface ButtonTheming {
  primaryBg: string;
  primaryText: string;
  primaryHover: string;
  secondaryBg: string;
  secondaryText: string;
  secondaryHover: string;
  borderRadius: number;
}

interface CardTheming {
  backgroundColor: string;
  borderRadius: number;
  shadow: string;
  hoverShadow: string;
  borderColor: string;
  borderWidth: number;
}

interface HeroTheming {
  overlayColor: string;
  overlayOpacity: number;
  minHeight: string;
  contentAlign: string;
}

interface ThemeTemplate {
  global: GlobalTheming & {
    primaryColor?: string;
    primaryHoverColor?: string;
    accentColor?: string;
    accentHoverColor?: string;
    sectionPaddingY?: number;
    sectionPaddingX?: number;
    headingColor?: string;
    headingFontSize?: number;
    headingFontStyle?: string;
    headingFontWeight?: string | number;
    headingLetterSpacing?: number;
    headingLineHeight?: number;
    headingTextDecoration?: string;
    headingTextTransform?: string;
  };
  navigation: NavigationTheming & {
    textColor?: string;
    textHoverColor?: string;
    activeColor?: string;
    backdropBlur?: boolean;
    dropdownBg?: string;
    dropdownTextColor?: string;
  };
  buttons?: ButtonTheming;
  cards?: CardTheming;
  hero?: HeroTheming;
  footer?: FooterTheming;
}

interface ThemeTemplateMeta {
  id: string;
  name: string;
  description: string;
  previewColors: { bg: string; primary: string; text: string };
}

interface Language {
  code: string;
  name: string;
}

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, NotificationComponent]
})
export class SettingsComponent implements OnInit {
  currentUser: Observable<User | null>;
  websites: any[] = [];
  loading: boolean = true;
  errorMessage: string = '';
  currentDomain: string | null = null;
  settingsForm!: FormGroup;
  availableLanguages: Language[] = [
    { code: 'nl', name: 'Nederlands' },
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'it', name: 'Italiano' },
    { code: 'pt', name: 'Português' },
  ];
  logoUploading: boolean = false;
  faviconUploading: boolean = false;
  savingAll: boolean = false;
  newLanguageForm!: FormGroup;
  showAddLanguageForm: boolean = false;
  showAddLanguageModal: boolean = false;
  countrySearchTerm: string = '';
  filteredCountries: { code: string; name: string; languageCode: string; languageName: string }[] = [];
  allCountries: { code: string; name: string; languageCode: string; languageName: string }[] = [];

  constructor(
    private websiteService: WebsiteService,
    private authService: AuthService,
    private router: Router,
    private fb: FormBuilder,
    private firestore: Firestore,
    private storage: Storage,
    public notificationService: NotificationService,
    private http: HttpClient,
    private imageUploadService: ImageUploadService
  ) {
    this.currentUser = this.authService.currentUser$;
    this.initForm();
    this.initNewLanguageForm();
    
    this.websiteService.domainChange$.subscribe(() => {
      this.currentDomain = this.websiteService.getCurrentDomain();
      if (!this.currentDomain) {
        this.router.navigate(['/']);
        return;
      }
      this.loadData();
    });
  }

  themingForm!: FormGroup;
  globalThemingForm!: FormGroup;
  topbarForm!: FormGroup;
  footerForm!: FormGroup;
  footerLogoUploading: boolean = false;
  footerColumns: { title: string; links: { label: string; url: string }[] }[] = [];
  footerBottomLinks: { label: string; url: string }[] = [];

  // Website notification banner
  websiteBannerEnabled = false;
  websiteBannerTextNl = '';
  websiteBannerTextEn = '';
  websiteBannerTextDe = '';
  websiteBannerTextFr = '';
  websiteBannerTextEs = '';
  websiteBannerBgColor = '#1e40af';
  websiteBannerTextColor = '#ffffff';

  // Shop notification banner
  shopBannerEnabled = false;
  shopBannerTextNl = '';
  shopBannerTextEn = '';
  shopBannerTextDe = '';
  shopBannerTextFr = '';
  shopBannerTextEs = '';
  shopBannerBgColor = '#87bbc7';
  shopBannerTextColor = '#ffffff';

  availableFonts = [
    { value: 'system', label: 'System Default' },
    // Google Fonts
    { value: 'Poppins, sans-serif', label: 'Poppins (Google)' },
    { value: 'Inter, sans-serif', label: 'Inter (Google)' },
    { value: 'Roboto, sans-serif', label: 'Roboto (Google)' },
    { value: 'Open Sans, sans-serif', label: 'Open Sans (Google)' },
    { value: 'Montserrat, sans-serif', label: 'Montserrat (Google)' },
    { value: 'Lato, sans-serif', label: 'Lato (Google)' },
    { value: 'Raleway, sans-serif', label: 'Raleway (Google)' },
    { value: 'Source Sans Pro, sans-serif', label: 'Source Sans Pro (Google)' },
    { value: 'DM Sans, sans-serif', label: 'DM Sans (Google)' },
    { value: 'Nunito, sans-serif', label: 'Nunito (Google)' },
    { value: 'Playfair Display, serif', label: 'Playfair Display (Google)' },
    { value: 'Merriweather, serif', label: 'Merriweather (Google)' },
    { value: 'IBM Plex Sans, sans-serif', label: 'IBM Plex Sans (Google)' },
    // System fonts
    { value: 'Arial, sans-serif', label: 'Arial' },
    { value: 'Helvetica, sans-serif', label: 'Helvetica' },
    { value: 'Georgia, serif', label: 'Georgia' },
    { value: 'Verdana, sans-serif', label: 'Verdana' },
    { value: 'Tahoma, sans-serif', label: 'Tahoma' },
    { value: 'Trebuchet MS, sans-serif', label: 'Trebuchet MS' },
    { value: 'Palatino, serif', label: 'Palatino' },
    { value: 'Garamond, serif', label: 'Garamond' }
  ];

  headingLevels = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;
  expandedHeadings: { [key: string]: boolean } = {
    h1: true,
    h2: false,
    h3: false,
    h4: false,
    h5: false,
    h6: false
  };

  selectedTemplate: string = '';

  themeTemplates: ThemeTemplateMeta[] = [
    { id: 'modern-clean', name: 'Modern Clean', description: 'Wit en lichtgrijs met blauw accent. Professioneel, minimalistisch design.', previewColors: { bg: '#ffffff', primary: '#2563eb', text: '#374151' } },
    { id: 'bold-dark', name: 'Bold Dark', description: 'Donkere achtergrond met cyan accent. Hoog contrast, futuristisch.', previewColors: { bg: '#0f172a', primary: '#06b6d4', text: '#94a3b8' } },
    { id: 'nature', name: 'Nature', description: 'Warme witte achtergrond met bosgroen accent. Organisch en uitnodigend.', previewColors: { bg: '#fefce8', primary: '#166534', text: '#3f3f2e' } },
    { id: 'corporate', name: 'Corporate', description: 'Lichtgrijze achtergrond met navy accent. Professionele zakelijke uitstraling.', previewColors: { bg: '#f8fafc', primary: '#1e3a5f', text: '#334155' } },
    { id: 'elegant', name: 'Elegant', description: 'Cr\u00e8me achtergrond met goud accent. Serif typografie, luxueus.', previewColors: { bg: '#faf7f2', primary: '#b8860b', text: '#44403c' } },
    { id: 'tech', name: 'Tech', description: 'Bijna-zwarte achtergrond met neongroen accent. Scherp en futuristisch.', previewColors: { bg: '#0a0a0a', primary: '#00ff88', text: '#a1a1aa' } },
    { id: 'warm', name: 'Warm', description: 'Warme witte achtergrond met terracotta accent. Afgerond en gezellig.', previewColors: { bg: '#fffbf5', primary: '#c2410c', text: '#57534e' } },
    { id: 'ocean', name: 'Ocean', description: 'Diepblauw met turquoise accent. Fris en helder, waterinspiratie.', previewColors: { bg: '#f0f9ff', primary: '#0369a1', text: '#334155' } },
    { id: 'berry', name: 'Berry', description: 'Lichte achtergrond met paars-roze accent. Speels en creatief.', previewColors: { bg: '#fdf4ff', primary: '#a21caf', text: '#4a044e' } },
    { id: 'slate', name: 'Slate', description: 'Neutraal grijs met staalblauwe accenten. Strak en tijdloos.', previewColors: { bg: '#f8fafc', primary: '#475569', text: '#1e293b' } },
    { id: 'forest', name: 'Forest', description: 'Donkergroene basis met lichtgroene accenten. Robuust en betrouwbaar.', previewColors: { bg: '#1a2e1a', primary: '#4ade80', text: '#d1d5db' } },
    { id: 'sunset', name: 'Sunset', description: 'Warm wit met oranje-roze gradient accent. Energiek en uitnodigend.', previewColors: { bg: '#fffbeb', primary: '#e11d48', text: '#451a03' } },
    { id: 'industrial', name: 'Industrial', description: 'Donkergrijs met geel accent. Stoer, ruw en no-nonsense.', previewColors: { bg: '#18181b', primary: '#eab308', text: '#d4d4d8' } },
    { id: 'dwt-water', name: 'DWT Water', description: 'Poppins font, blauw accent op wit. Licht en professioneel, DutchWaterTech stijl.', previewColors: { bg: '#ffffff', primary: '#007bff', text: '#212529' } },
  ];

  private templateData: Record<string, ThemeTemplate> = {
    'modern-clean': {
      global: { backgroundColor: '#ffffff', bodyFontSize: 16, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", headingColor: '#111827', headingFontSize: 32, headingFontStyle: 'normal', headingFontWeight: 700, headingLetterSpacing: -0.025, headingLineHeight: 1.2, headingTextDecoration: 'none', headingTextTransform: 'none', headings: { h1: { color: '#111827', fontSize: 48, fontWeight: '800', fontStyle: 'normal', letterSpacing: -0.03, lineHeight: 1.1, textDecoration: 'none', textTransform: 'none' }, h2: { color: '#111827', fontSize: 36, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.025, lineHeight: 1.15, textDecoration: 'none', textTransform: 'none' }, h3: { color: '#111827', fontSize: 28, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.02, lineHeight: 1.2, textDecoration: 'none', textTransform: 'none' }, h4: { color: '#1f2937', fontSize: 22, fontWeight: '600', fontStyle: 'normal', letterSpacing: -0.01, lineHeight: 1.25, textDecoration: 'none', textTransform: 'none' }, h5: { color: '#1f2937', fontSize: 18, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.3, textDecoration: 'none', textTransform: 'none' }, h6: { color: '#374151', fontSize: 16, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0.02, lineHeight: 1.35, textDecoration: 'none', textTransform: 'none' } }, lineHeight: 1.6, linkColor: '#2563eb', linkHoverColor: '#1d4ed8', textColor: '#374151', primaryColor: '#2563eb', primaryHoverColor: '#1d4ed8', accentColor: '#7c3aed', accentHoverColor: '#6d28d9', sectionPaddingY: 64, sectionPaddingX: 24 },
      navigation: { backgroundColor: '#ffffff', textColor: '#374151', textHoverColor: '#2563eb', activeColor: '#2563eb', borderColor: '#e5e7eb', borderEnabled: true, borderStyle: 'solid', borderWidth: 1, height: 64, logoPosition: 'left', marginLeft: 24, marginRight: 24, menuPosition: 'right', padding: 16, backdropBlur: true, dropdownBg: '#ffffff', dropdownTextColor: '#374151' },
      buttons: { primaryBg: '#2563eb', primaryText: '#ffffff', primaryHover: '#1d4ed8', secondaryBg: '#f3f4f6', secondaryText: '#374151', secondaryHover: '#e5e7eb', borderRadius: 8 },
      cards: { backgroundColor: '#ffffff', borderRadius: 12, shadow: '0 1px 3px rgba(0,0,0,0.08)', hoverShadow: '0 10px 25px rgba(0,0,0,0.08)', borderColor: '#e5e7eb', borderWidth: 1 },
      hero: { overlayColor: '#000000', overlayOpacity: 0.4, minHeight: '540px', contentAlign: 'center' },
      footer: { backgroundColor: '#111827', textColor: '#d1d5db', padding: 48 },
    },
    'bold-dark': {
      global: { backgroundColor: '#0f172a', bodyFontSize: 16, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", headingColor: '#f1f5f9', headingFontSize: 34, headingFontStyle: 'normal', headingFontWeight: 800, headingLetterSpacing: -0.03, headingLineHeight: 1.15, headingTextDecoration: 'none', headingTextTransform: 'none', headings: { h1: { color: '#f1f5f9', fontSize: 52, fontWeight: '900', fontStyle: 'normal', letterSpacing: -0.04, lineHeight: 1.05, textDecoration: 'none', textTransform: 'none' }, h2: { color: '#f1f5f9', fontSize: 40, fontWeight: '800', fontStyle: 'normal', letterSpacing: -0.03, lineHeight: 1.1, textDecoration: 'none', textTransform: 'none' }, h3: { color: '#e2e8f0', fontSize: 30, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.02, lineHeight: 1.15, textDecoration: 'none', textTransform: 'none' }, h4: { color: '#e2e8f0', fontSize: 24, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.01, lineHeight: 1.2, textDecoration: 'none', textTransform: 'none' }, h5: { color: '#cbd5e1', fontSize: 20, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.25, textDecoration: 'none', textTransform: 'none' }, h6: { color: '#cbd5e1', fontSize: 16, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0.02, lineHeight: 1.3, textDecoration: 'none', textTransform: 'uppercase' } }, lineHeight: 1.65, linkColor: '#06b6d4', linkHoverColor: '#22d3ee', textColor: '#94a3b8', primaryColor: '#06b6d4', primaryHoverColor: '#22d3ee', accentColor: '#8b5cf6', accentHoverColor: '#a78bfa', sectionPaddingY: 72, sectionPaddingX: 24 },
      navigation: { backgroundColor: '#0f172a', textColor: '#94a3b8', textHoverColor: '#06b6d4', activeColor: '#06b6d4', borderColor: '#1e293b', borderEnabled: true, borderStyle: 'solid', borderWidth: 1, height: 68, logoPosition: 'left', marginLeft: 24, marginRight: 24, menuPosition: 'right', padding: 16, backdropBlur: true, dropdownBg: '#1e293b', dropdownTextColor: '#e2e8f0' },
      buttons: { primaryBg: '#06b6d4', primaryText: '#0f172a', primaryHover: '#22d3ee', secondaryBg: '#1e293b', secondaryText: '#e2e8f0', secondaryHover: '#334155', borderRadius: 6 },
      cards: { backgroundColor: '#1e293b', borderRadius: 10, shadow: '0 4px 12px rgba(0,0,0,0.3)', hoverShadow: '0 12px 32px rgba(6,182,212,0.12)', borderColor: '#334155', borderWidth: 1 },
      hero: { overlayColor: '#0f172a', overlayOpacity: 0.6, minHeight: '580px', contentAlign: 'center' },
      footer: { backgroundColor: '#020617', textColor: '#64748b', padding: 48 },
    },
    'nature': {
      global: { backgroundColor: '#fefce8', bodyFontSize: 16, fontFamily: "'Source Sans Pro', 'Nunito Sans', -apple-system, sans-serif", headingColor: '#14532d', headingFontSize: 32, headingFontStyle: 'normal', headingFontWeight: 700, headingLetterSpacing: -0.015, headingLineHeight: 1.25, headingTextDecoration: 'none', headingTextTransform: 'none', headings: { h1: { color: '#14532d', fontSize: 46, fontWeight: '800', fontStyle: 'normal', letterSpacing: -0.025, lineHeight: 1.15, textDecoration: 'none', textTransform: 'none' }, h2: { color: '#14532d', fontSize: 34, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.02, lineHeight: 1.2, textDecoration: 'none', textTransform: 'none' }, h3: { color: '#166534', fontSize: 26, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.01, lineHeight: 1.25, textDecoration: 'none', textTransform: 'none' }, h4: { color: '#166534', fontSize: 22, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.3, textDecoration: 'none', textTransform: 'none' }, h5: { color: '#15803d', fontSize: 18, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.35, textDecoration: 'none', textTransform: 'none' }, h6: { color: '#15803d', fontSize: 15, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0.01, lineHeight: 1.4, textDecoration: 'none', textTransform: 'none' } }, lineHeight: 1.7, linkColor: '#166534', linkHoverColor: '#15803d', textColor: '#3f3f2e', primaryColor: '#166534', primaryHoverColor: '#15803d', accentColor: '#a16207', accentHoverColor: '#ca8a04', sectionPaddingY: 60, sectionPaddingX: 24 },
      navigation: { backgroundColor: '#fefce8', textColor: '#3f3f2e', textHoverColor: '#166534', activeColor: '#166534', borderColor: '#d9f99d', borderEnabled: true, borderStyle: 'solid', borderWidth: 1, height: 64, logoPosition: 'left', marginLeft: 24, marginRight: 24, menuPosition: 'right', padding: 16, backdropBlur: false, dropdownBg: '#fefce8', dropdownTextColor: '#3f3f2e' },
      buttons: { primaryBg: '#166534', primaryText: '#ffffff', primaryHover: '#15803d', secondaryBg: '#ecfccb', secondaryText: '#166534', secondaryHover: '#d9f99d', borderRadius: 10 },
      cards: { backgroundColor: '#fffef5', borderRadius: 14, shadow: '0 2px 8px rgba(22,101,52,0.06)', hoverShadow: '0 8px 24px rgba(22,101,52,0.1)', borderColor: '#d9f99d', borderWidth: 1 },
      hero: { overlayColor: '#14532d', overlayOpacity: 0.35, minHeight: '520px', contentAlign: 'center' },
      footer: { backgroundColor: '#14532d', textColor: '#bbf7d0', padding: 48 },
    },
    'corporate': {
      global: { backgroundColor: '#f8fafc', bodyFontSize: 16, fontFamily: "'Merriweather Sans', 'Open Sans', -apple-system, sans-serif", headingColor: '#1e3a5f', headingFontSize: 32, headingFontStyle: 'normal', headingFontWeight: 700, headingLetterSpacing: -0.01, headingLineHeight: 1.2, headingTextDecoration: 'none', headingTextTransform: 'none', headings: { h1: { color: '#1e3a5f', fontSize: 44, fontWeight: '800', fontStyle: 'normal', letterSpacing: -0.02, lineHeight: 1.1, textDecoration: 'none', textTransform: 'none' }, h2: { color: '#1e3a5f', fontSize: 34, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.015, lineHeight: 1.15, textDecoration: 'none', textTransform: 'none' }, h3: { color: '#1e3a5f', fontSize: 26, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.01, lineHeight: 1.2, textDecoration: 'none', textTransform: 'none' }, h4: { color: '#2c5282', fontSize: 22, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.25, textDecoration: 'none', textTransform: 'none' }, h5: { color: '#2c5282', fontSize: 18, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.3, textDecoration: 'none', textTransform: 'none' }, h6: { color: '#2c5282', fontSize: 14, fontWeight: '700', fontStyle: 'normal', letterSpacing: 0.05, lineHeight: 1.35, textDecoration: 'none', textTransform: 'uppercase' } }, lineHeight: 1.6, linkColor: '#1e3a5f', linkHoverColor: '#2c5282', textColor: '#334155', primaryColor: '#1e3a5f', primaryHoverColor: '#2c5282', accentColor: '#b91c1c', accentHoverColor: '#dc2626', sectionPaddingY: 64, sectionPaddingX: 32 },
      navigation: { backgroundColor: '#ffffff', textColor: '#334155', textHoverColor: '#1e3a5f', activeColor: '#1e3a5f', borderColor: '#e2e8f0', borderEnabled: true, borderStyle: 'solid', borderWidth: 1, height: 72, logoPosition: 'left', marginLeft: 32, marginRight: 32, menuPosition: 'right', padding: 20, backdropBlur: false, dropdownBg: '#ffffff', dropdownTextColor: '#334155' },
      buttons: { primaryBg: '#1e3a5f', primaryText: '#ffffff', primaryHover: '#2c5282', secondaryBg: '#e2e8f0', secondaryText: '#1e3a5f', secondaryHover: '#cbd5e1', borderRadius: 4 },
      cards: { backgroundColor: '#ffffff', borderRadius: 6, shadow: '0 1px 4px rgba(0,0,0,0.06)', hoverShadow: '0 6px 20px rgba(0,0,0,0.08)', borderColor: '#e2e8f0', borderWidth: 1 },
      hero: { overlayColor: '#1e3a5f', overlayOpacity: 0.5, minHeight: '500px', contentAlign: 'left' },
      footer: { backgroundColor: '#1e3a5f', textColor: '#cbd5e1', padding: 56 },
    },
    'elegant': {
      global: { backgroundColor: '#faf7f2', bodyFontSize: 17, fontFamily: "'Georgia', 'Palatino Linotype', 'Book Antiqua', Palatino, serif", headingColor: '#292524', headingFontSize: 32, headingFontStyle: 'normal', headingFontWeight: 600, headingLetterSpacing: 0.01, headingLineHeight: 1.25, headingTextDecoration: 'none', headingTextTransform: 'none', headings: { h1: { color: '#292524', fontSize: 44, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0.005, lineHeight: 1.15, textDecoration: 'none', textTransform: 'none' }, h2: { color: '#292524', fontSize: 34, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0.005, lineHeight: 1.2, textDecoration: 'none', textTransform: 'none' }, h3: { color: '#44403c', fontSize: 26, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0.01, lineHeight: 1.25, textDecoration: 'none', textTransform: 'none' }, h4: { color: '#44403c', fontSize: 22, fontWeight: '500', fontStyle: 'italic', letterSpacing: 0.01, lineHeight: 1.3, textDecoration: 'none', textTransform: 'none' }, h5: { color: '#57534e', fontSize: 18, fontWeight: '500', fontStyle: 'normal', letterSpacing: 0.02, lineHeight: 1.35, textDecoration: 'none', textTransform: 'none' }, h6: { color: '#57534e', fontSize: 14, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0.08, lineHeight: 1.4, textDecoration: 'none', textTransform: 'uppercase' } }, lineHeight: 1.75, linkColor: '#b8860b', linkHoverColor: '#996f09', textColor: '#44403c', primaryColor: '#b8860b', primaryHoverColor: '#996f09', accentColor: '#78350f', accentHoverColor: '#92400e', sectionPaddingY: 72, sectionPaddingX: 32 },
      navigation: { backgroundColor: '#faf7f2', textColor: '#44403c', textHoverColor: '#b8860b', activeColor: '#b8860b', borderColor: '#e7e0d5', borderEnabled: true, borderStyle: 'solid', borderWidth: 1, height: 72, logoPosition: 'center', marginLeft: 32, marginRight: 32, menuPosition: 'center', padding: 20, backdropBlur: false, dropdownBg: '#faf7f2', dropdownTextColor: '#44403c' },
      buttons: { primaryBg: '#b8860b', primaryText: '#ffffff', primaryHover: '#996f09', secondaryBg: 'transparent', secondaryText: '#b8860b', secondaryHover: '#faf5eb', borderRadius: 2 },
      cards: { backgroundColor: '#ffffff', borderRadius: 4, shadow: '0 1px 2px rgba(0,0,0,0.04)', hoverShadow: '0 4px 16px rgba(0,0,0,0.06)', borderColor: '#e7e0d5', borderWidth: 1 },
      hero: { overlayColor: '#292524', overlayOpacity: 0.3, minHeight: '600px', contentAlign: 'center' },
      footer: { backgroundColor: '#292524', textColor: '#a8a29e', padding: 56 },
    },
    'tech': {
      global: { backgroundColor: '#0a0a0a', bodyFontSize: 15, fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", headingColor: '#e4e4e7', headingFontSize: 32, headingFontStyle: 'normal', headingFontWeight: 700, headingLetterSpacing: -0.03, headingLineHeight: 1.15, headingTextDecoration: 'none', headingTextTransform: 'none', headings: { h1: { color: '#00ff88', fontSize: 50, fontWeight: '800', fontStyle: 'normal', letterSpacing: -0.04, lineHeight: 1.05, textDecoration: 'none', textTransform: 'none' }, h2: { color: '#e4e4e7', fontSize: 38, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.03, lineHeight: 1.1, textDecoration: 'none', textTransform: 'none' }, h3: { color: '#e4e4e7', fontSize: 28, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.02, lineHeight: 1.15, textDecoration: 'none', textTransform: 'none' }, h4: { color: '#a1a1aa', fontSize: 22, fontWeight: '600', fontStyle: 'normal', letterSpacing: -0.01, lineHeight: 1.2, textDecoration: 'none', textTransform: 'none' }, h5: { color: '#a1a1aa', fontSize: 18, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.25, textDecoration: 'none', textTransform: 'none' }, h6: { color: '#00ff88', fontSize: 13, fontWeight: '700', fontStyle: 'normal', letterSpacing: 0.1, lineHeight: 1.35, textDecoration: 'none', textTransform: 'uppercase' } }, lineHeight: 1.6, linkColor: '#00ff88', linkHoverColor: '#33ffaa', textColor: '#a1a1aa', primaryColor: '#00ff88', primaryHoverColor: '#33ffaa', accentColor: '#a855f7', accentHoverColor: '#c084fc', sectionPaddingY: 64, sectionPaddingX: 24 },
      navigation: { backgroundColor: '#0a0a0a', textColor: '#a1a1aa', textHoverColor: '#00ff88', activeColor: '#00ff88', borderColor: '#27272a', borderEnabled: true, borderStyle: 'solid', borderWidth: 1, height: 60, logoPosition: 'left', marginLeft: 24, marginRight: 24, menuPosition: 'right', padding: 14, backdropBlur: true, dropdownBg: '#18181b', dropdownTextColor: '#e4e4e7' },
      buttons: { primaryBg: '#00ff88', primaryText: '#0a0a0a', primaryHover: '#33ffaa', secondaryBg: '#27272a', secondaryText: '#e4e4e7', secondaryHover: '#3f3f46', borderRadius: 0 },
      cards: { backgroundColor: '#18181b', borderRadius: 2, shadow: '0 0 0 1px rgba(0,255,136,0.06)', hoverShadow: '0 0 20px rgba(0,255,136,0.1)', borderColor: '#27272a', borderWidth: 1 },
      hero: { overlayColor: '#0a0a0a', overlayOpacity: 0.7, minHeight: '560px', contentAlign: 'center' },
      footer: { backgroundColor: '#09090b', textColor: '#71717a', padding: 40 },
    },
    'warm': {
      global: { backgroundColor: '#fffbf5', bodyFontSize: 16, fontFamily: "'DM Sans', 'Nunito', -apple-system, BlinkMacSystemFont, sans-serif", headingColor: '#431407', headingFontSize: 32, headingFontStyle: 'normal', headingFontWeight: 700, headingLetterSpacing: -0.015, headingLineHeight: 1.2, headingTextDecoration: 'none', headingTextTransform: 'none', headings: { h1: { color: '#431407', fontSize: 46, fontWeight: '800', fontStyle: 'normal', letterSpacing: -0.025, lineHeight: 1.1, textDecoration: 'none', textTransform: 'none' }, h2: { color: '#431407', fontSize: 34, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.02, lineHeight: 1.15, textDecoration: 'none', textTransform: 'none' }, h3: { color: '#7c2d12', fontSize: 26, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.01, lineHeight: 1.2, textDecoration: 'none', textTransform: 'none' }, h4: { color: '#7c2d12', fontSize: 22, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.25, textDecoration: 'none', textTransform: 'none' }, h5: { color: '#9a3412', fontSize: 18, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.3, textDecoration: 'none', textTransform: 'none' }, h6: { color: '#9a3412', fontSize: 15, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0.01, lineHeight: 1.35, textDecoration: 'none', textTransform: 'none' } }, lineHeight: 1.7, linkColor: '#c2410c', linkHoverColor: '#ea580c', textColor: '#57534e', primaryColor: '#c2410c', primaryHoverColor: '#ea580c', accentColor: '#b45309', accentHoverColor: '#d97706', sectionPaddingY: 60, sectionPaddingX: 24 },
      navigation: { backgroundColor: '#fffbf5', textColor: '#57534e', textHoverColor: '#c2410c', activeColor: '#c2410c', borderColor: '#fed7aa', borderEnabled: true, borderStyle: 'solid', borderWidth: 1, height: 64, logoPosition: 'left', marginLeft: 24, marginRight: 24, menuPosition: 'right', padding: 16, backdropBlur: false, dropdownBg: '#fffbf5', dropdownTextColor: '#57534e' },
      buttons: { primaryBg: '#c2410c', primaryText: '#ffffff', primaryHover: '#ea580c', secondaryBg: '#fff7ed', secondaryText: '#c2410c', secondaryHover: '#ffedd5', borderRadius: 12 },
      cards: { backgroundColor: '#ffffff', borderRadius: 16, shadow: '0 2px 8px rgba(194,65,12,0.06)', hoverShadow: '0 10px 30px rgba(194,65,12,0.1)', borderColor: '#fed7aa', borderWidth: 1 },
      hero: { overlayColor: '#431407', overlayOpacity: 0.35, minHeight: '520px', contentAlign: 'center' },
      footer: { backgroundColor: '#431407', textColor: '#fdba74', padding: 48 },
    },
    'ocean': {
      global: { backgroundColor: '#f0f9ff', bodyFontSize: 16, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", headingColor: '#0c4a6e', headingFontSize: 32, headingFontStyle: 'normal', headingFontWeight: 700, headingLetterSpacing: -0.02, headingLineHeight: 1.2, headingTextDecoration: 'none', headingTextTransform: 'none', headings: { h1: { color: '#0c4a6e', fontSize: 48, fontWeight: '800', fontStyle: 'normal', letterSpacing: -0.03, lineHeight: 1.1, textDecoration: 'none', textTransform: 'none' }, h2: { color: '#0c4a6e', fontSize: 36, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.025, lineHeight: 1.15, textDecoration: 'none', textTransform: 'none' }, h3: { color: '#075985', fontSize: 28, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.015, lineHeight: 1.2, textDecoration: 'none', textTransform: 'none' }, h4: { color: '#075985', fontSize: 22, fontWeight: '600', fontStyle: 'normal', letterSpacing: -0.01, lineHeight: 1.25, textDecoration: 'none', textTransform: 'none' }, h5: { color: '#0369a1', fontSize: 18, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.3, textDecoration: 'none', textTransform: 'none' }, h6: { color: '#0369a1', fontSize: 14, fontWeight: '700', fontStyle: 'normal', letterSpacing: 0.04, lineHeight: 1.35, textDecoration: 'none', textTransform: 'uppercase' } }, lineHeight: 1.65, linkColor: '#0369a1', linkHoverColor: '#0284c7', textColor: '#334155', primaryColor: '#0369a1', primaryHoverColor: '#0284c7', accentColor: '#0891b2', accentHoverColor: '#06b6d4', sectionPaddingY: 64, sectionPaddingX: 24 },
      navigation: { backgroundColor: '#ffffff', textColor: '#334155', textHoverColor: '#0369a1', activeColor: '#0369a1', borderColor: '#bae6fd', borderEnabled: true, borderStyle: 'solid', borderWidth: 1, height: 64, logoPosition: 'left', marginLeft: 24, marginRight: 24, menuPosition: 'right', padding: 16, backdropBlur: true, dropdownBg: '#ffffff', dropdownTextColor: '#334155' },
      buttons: { primaryBg: '#0369a1', primaryText: '#ffffff', primaryHover: '#0284c7', secondaryBg: '#e0f2fe', secondaryText: '#0369a1', secondaryHover: '#bae6fd', borderRadius: 8 },
      cards: { backgroundColor: '#ffffff', borderRadius: 12, shadow: '0 1px 4px rgba(3,105,161,0.06)', hoverShadow: '0 8px 24px rgba(3,105,161,0.1)', borderColor: '#bae6fd', borderWidth: 1 },
      hero: { overlayColor: '#0c4a6e', overlayOpacity: 0.45, minHeight: '540px', contentAlign: 'center' },
      footer: { backgroundColor: '#0c4a6e', textColor: '#7dd3fc', padding: 48 },
    },
    'berry': {
      global: { backgroundColor: '#fdf4ff', bodyFontSize: 16, fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", headingColor: '#4a044e', headingFontSize: 32, headingFontStyle: 'normal', headingFontWeight: 700, headingLetterSpacing: -0.02, headingLineHeight: 1.2, headingTextDecoration: 'none', headingTextTransform: 'none', headings: { h1: { color: '#4a044e', fontSize: 46, fontWeight: '800', fontStyle: 'normal', letterSpacing: -0.03, lineHeight: 1.1, textDecoration: 'none', textTransform: 'none' }, h2: { color: '#4a044e', fontSize: 34, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.02, lineHeight: 1.15, textDecoration: 'none', textTransform: 'none' }, h3: { color: '#701a75', fontSize: 26, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.015, lineHeight: 1.2, textDecoration: 'none', textTransform: 'none' }, h4: { color: '#701a75', fontSize: 22, fontWeight: '600', fontStyle: 'normal', letterSpacing: -0.01, lineHeight: 1.25, textDecoration: 'none', textTransform: 'none' }, h5: { color: '#86198f', fontSize: 18, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.3, textDecoration: 'none', textTransform: 'none' }, h6: { color: '#a21caf', fontSize: 14, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0.03, lineHeight: 1.35, textDecoration: 'none', textTransform: 'uppercase' } }, lineHeight: 1.65, linkColor: '#a21caf', linkHoverColor: '#c026d3', textColor: '#4a044e', primaryColor: '#a21caf', primaryHoverColor: '#c026d3', accentColor: '#db2777', accentHoverColor: '#ec4899', sectionPaddingY: 64, sectionPaddingX: 24 },
      navigation: { backgroundColor: '#ffffff', textColor: '#4a044e', textHoverColor: '#a21caf', activeColor: '#a21caf', borderColor: '#f0abfc', borderEnabled: true, borderStyle: 'solid', borderWidth: 1, height: 64, logoPosition: 'left', marginLeft: 24, marginRight: 24, menuPosition: 'right', padding: 16, backdropBlur: true, dropdownBg: '#ffffff', dropdownTextColor: '#4a044e' },
      buttons: { primaryBg: '#a21caf', primaryText: '#ffffff', primaryHover: '#c026d3', secondaryBg: '#fae8ff', secondaryText: '#a21caf', secondaryHover: '#f5d0fe', borderRadius: 10 },
      cards: { backgroundColor: '#ffffff', borderRadius: 14, shadow: '0 2px 6px rgba(162,28,175,0.06)', hoverShadow: '0 8px 24px rgba(162,28,175,0.1)', borderColor: '#f0abfc', borderWidth: 1 },
      hero: { overlayColor: '#4a044e', overlayOpacity: 0.4, minHeight: '540px', contentAlign: 'center' },
      footer: { backgroundColor: '#4a044e', textColor: '#e879f9', padding: 48 },
    },
    'slate': {
      global: { backgroundColor: '#f8fafc', bodyFontSize: 16, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", headingColor: '#0f172a', headingFontSize: 32, headingFontStyle: 'normal', headingFontWeight: 700, headingLetterSpacing: -0.02, headingLineHeight: 1.2, headingTextDecoration: 'none', headingTextTransform: 'none', headings: { h1: { color: '#0f172a', fontSize: 46, fontWeight: '800', fontStyle: 'normal', letterSpacing: -0.03, lineHeight: 1.1, textDecoration: 'none', textTransform: 'none' }, h2: { color: '#0f172a', fontSize: 34, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.025, lineHeight: 1.15, textDecoration: 'none', textTransform: 'none' }, h3: { color: '#1e293b', fontSize: 26, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.015, lineHeight: 1.2, textDecoration: 'none', textTransform: 'none' }, h4: { color: '#1e293b', fontSize: 22, fontWeight: '600', fontStyle: 'normal', letterSpacing: -0.01, lineHeight: 1.25, textDecoration: 'none', textTransform: 'none' }, h5: { color: '#334155', fontSize: 18, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.3, textDecoration: 'none', textTransform: 'none' }, h6: { color: '#475569', fontSize: 13, fontWeight: '700', fontStyle: 'normal', letterSpacing: 0.06, lineHeight: 1.35, textDecoration: 'none', textTransform: 'uppercase' } }, lineHeight: 1.6, linkColor: '#475569', linkHoverColor: '#334155', textColor: '#1e293b', primaryColor: '#475569', primaryHoverColor: '#334155', accentColor: '#6366f1', accentHoverColor: '#818cf8', sectionPaddingY: 64, sectionPaddingX: 24 },
      navigation: { backgroundColor: '#ffffff', textColor: '#475569', textHoverColor: '#0f172a', activeColor: '#0f172a', borderColor: '#e2e8f0', borderEnabled: true, borderStyle: 'solid', borderWidth: 1, height: 64, logoPosition: 'left', marginLeft: 24, marginRight: 24, menuPosition: 'right', padding: 16, backdropBlur: true, dropdownBg: '#ffffff', dropdownTextColor: '#475569' },
      buttons: { primaryBg: '#475569', primaryText: '#ffffff', primaryHover: '#334155', secondaryBg: '#f1f5f9', secondaryText: '#475569', secondaryHover: '#e2e8f0', borderRadius: 6 },
      cards: { backgroundColor: '#ffffff', borderRadius: 8, shadow: '0 1px 3px rgba(0,0,0,0.05)', hoverShadow: '0 6px 20px rgba(0,0,0,0.07)', borderColor: '#e2e8f0', borderWidth: 1 },
      hero: { overlayColor: '#0f172a', overlayOpacity: 0.5, minHeight: '520px', contentAlign: 'left' },
      footer: { backgroundColor: '#0f172a', textColor: '#94a3b8', padding: 48 },
    },
    'forest': {
      global: { backgroundColor: '#1a2e1a', bodyFontSize: 16, fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif", headingColor: '#dcfce7', headingFontSize: 32, headingFontStyle: 'normal', headingFontWeight: 700, headingLetterSpacing: -0.02, headingLineHeight: 1.2, headingTextDecoration: 'none', headingTextTransform: 'none', headings: { h1: { color: '#4ade80', fontSize: 48, fontWeight: '800', fontStyle: 'normal', letterSpacing: -0.03, lineHeight: 1.1, textDecoration: 'none', textTransform: 'none' }, h2: { color: '#dcfce7', fontSize: 36, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.025, lineHeight: 1.15, textDecoration: 'none', textTransform: 'none' }, h3: { color: '#dcfce7', fontSize: 28, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.015, lineHeight: 1.2, textDecoration: 'none', textTransform: 'none' }, h4: { color: '#bbf7d0', fontSize: 22, fontWeight: '600', fontStyle: 'normal', letterSpacing: -0.01, lineHeight: 1.25, textDecoration: 'none', textTransform: 'none' }, h5: { color: '#bbf7d0', fontSize: 18, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.3, textDecoration: 'none', textTransform: 'none' }, h6: { color: '#4ade80', fontSize: 13, fontWeight: '700', fontStyle: 'normal', letterSpacing: 0.08, lineHeight: 1.35, textDecoration: 'none', textTransform: 'uppercase' } }, lineHeight: 1.65, linkColor: '#4ade80', linkHoverColor: '#86efac', textColor: '#d1d5db', primaryColor: '#4ade80', primaryHoverColor: '#86efac', accentColor: '#22d3ee', accentHoverColor: '#67e8f9', sectionPaddingY: 64, sectionPaddingX: 24 },
      navigation: { backgroundColor: '#14261a', textColor: '#a7f3d0', textHoverColor: '#4ade80', activeColor: '#4ade80', borderColor: '#2d4a2d', borderEnabled: true, borderStyle: 'solid', borderWidth: 1, height: 64, logoPosition: 'left', marginLeft: 24, marginRight: 24, menuPosition: 'right', padding: 16, backdropBlur: true, dropdownBg: '#1a3320', dropdownTextColor: '#a7f3d0' },
      buttons: { primaryBg: '#4ade80', primaryText: '#14261a', primaryHover: '#86efac', secondaryBg: '#2d4a2d', secondaryText: '#a7f3d0', secondaryHover: '#3d6a3d', borderRadius: 6 },
      cards: { backgroundColor: '#1f3a1f', borderRadius: 10, shadow: '0 2px 8px rgba(0,0,0,0.3)', hoverShadow: '0 8px 24px rgba(74,222,128,0.08)', borderColor: '#2d4a2d', borderWidth: 1 },
      hero: { overlayColor: '#0a1a0a', overlayOpacity: 0.6, minHeight: '560px', contentAlign: 'center' },
      footer: { backgroundColor: '#0f1f0f', textColor: '#6ee7b7', padding: 48 },
    },
    'sunset': {
      global: { backgroundColor: '#fffbeb', bodyFontSize: 16, fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", headingColor: '#451a03', headingFontSize: 32, headingFontStyle: 'normal', headingFontWeight: 700, headingLetterSpacing: -0.02, headingLineHeight: 1.2, headingTextDecoration: 'none', headingTextTransform: 'none', headings: { h1: { color: '#451a03', fontSize: 46, fontWeight: '800', fontStyle: 'normal', letterSpacing: -0.025, lineHeight: 1.1, textDecoration: 'none', textTransform: 'none' }, h2: { color: '#451a03', fontSize: 34, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.02, lineHeight: 1.15, textDecoration: 'none', textTransform: 'none' }, h3: { color: '#78350f', fontSize: 26, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.015, lineHeight: 1.2, textDecoration: 'none', textTransform: 'none' }, h4: { color: '#78350f', fontSize: 22, fontWeight: '600', fontStyle: 'normal', letterSpacing: -0.01, lineHeight: 1.25, textDecoration: 'none', textTransform: 'none' }, h5: { color: '#92400e', fontSize: 18, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.3, textDecoration: 'none', textTransform: 'none' }, h6: { color: '#e11d48', fontSize: 14, fontWeight: '700', fontStyle: 'normal', letterSpacing: 0.04, lineHeight: 1.35, textDecoration: 'none', textTransform: 'uppercase' } }, lineHeight: 1.65, linkColor: '#e11d48', linkHoverColor: '#f43f5e', textColor: '#451a03', primaryColor: '#e11d48', primaryHoverColor: '#f43f5e', accentColor: '#ea580c', accentHoverColor: '#f97316', sectionPaddingY: 64, sectionPaddingX: 24 },
      navigation: { backgroundColor: '#ffffff', textColor: '#451a03', textHoverColor: '#e11d48', activeColor: '#e11d48', borderColor: '#fde68a', borderEnabled: true, borderStyle: 'solid', borderWidth: 1, height: 64, logoPosition: 'left', marginLeft: 24, marginRight: 24, menuPosition: 'right', padding: 16, backdropBlur: true, dropdownBg: '#ffffff', dropdownTextColor: '#451a03' },
      buttons: { primaryBg: '#e11d48', primaryText: '#ffffff', primaryHover: '#f43f5e', secondaryBg: '#fef3c7', secondaryText: '#e11d48', secondaryHover: '#fde68a', borderRadius: 10 },
      cards: { backgroundColor: '#ffffff', borderRadius: 14, shadow: '0 2px 6px rgba(225,29,72,0.06)', hoverShadow: '0 10px 28px rgba(225,29,72,0.1)', borderColor: '#fde68a', borderWidth: 1 },
      hero: { overlayColor: '#451a03', overlayOpacity: 0.4, minHeight: '540px', contentAlign: 'center' },
      footer: { backgroundColor: '#451a03', textColor: '#fbbf24', padding: 48 },
    },
    'industrial': {
      global: { backgroundColor: '#18181b', bodyFontSize: 15, fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif", headingColor: '#fafafa', headingFontSize: 32, headingFontStyle: 'normal', headingFontWeight: 700, headingLetterSpacing: -0.02, headingLineHeight: 1.15, headingTextDecoration: 'none', headingTextTransform: 'none', headings: { h1: { color: '#eab308', fontSize: 50, fontWeight: '900', fontStyle: 'normal', letterSpacing: -0.04, lineHeight: 1.05, textDecoration: 'none', textTransform: 'uppercase' }, h2: { color: '#fafafa', fontSize: 38, fontWeight: '800', fontStyle: 'normal', letterSpacing: -0.03, lineHeight: 1.1, textDecoration: 'none', textTransform: 'none' }, h3: { color: '#fafafa', fontSize: 28, fontWeight: '700', fontStyle: 'normal', letterSpacing: -0.02, lineHeight: 1.15, textDecoration: 'none', textTransform: 'none' }, h4: { color: '#d4d4d8', fontSize: 22, fontWeight: '600', fontStyle: 'normal', letterSpacing: -0.01, lineHeight: 1.2, textDecoration: 'none', textTransform: 'none' }, h5: { color: '#d4d4d8', fontSize: 18, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.25, textDecoration: 'none', textTransform: 'none' }, h6: { color: '#eab308', fontSize: 13, fontWeight: '800', fontStyle: 'normal', letterSpacing: 0.1, lineHeight: 1.35, textDecoration: 'none', textTransform: 'uppercase' } }, lineHeight: 1.6, linkColor: '#eab308', linkHoverColor: '#facc15', textColor: '#d4d4d8', primaryColor: '#eab308', primaryHoverColor: '#facc15', accentColor: '#f97316', accentHoverColor: '#fb923c', sectionPaddingY: 64, sectionPaddingX: 24 },
      navigation: { backgroundColor: '#0f0f12', textColor: '#a1a1aa', textHoverColor: '#eab308', activeColor: '#eab308', borderColor: '#27272a', borderEnabled: true, borderStyle: 'solid', borderWidth: 1, height: 60, logoPosition: 'left', marginLeft: 24, marginRight: 24, menuPosition: 'right', padding: 14, backdropBlur: true, dropdownBg: '#1c1c20', dropdownTextColor: '#d4d4d8' },
      buttons: { primaryBg: '#eab308', primaryText: '#18181b', primaryHover: '#facc15', secondaryBg: '#27272a', secondaryText: '#d4d4d8', secondaryHover: '#3f3f46', borderRadius: 2 },
      cards: { backgroundColor: '#1c1c20', borderRadius: 4, shadow: '0 0 0 1px rgba(234,179,8,0.08)', hoverShadow: '0 0 20px rgba(234,179,8,0.12)', borderColor: '#27272a', borderWidth: 1 },
      hero: { overlayColor: '#18181b', overlayOpacity: 0.7, minHeight: '560px', contentAlign: 'left' },
      footer: { backgroundColor: '#0f0f12', textColor: '#71717a', padding: 40 },
    },
    'dwt-water': {
      global: { backgroundColor: '#ffffff', bodyFontSize: 16, fontFamily: 'Poppins, sans-serif', headingColor: '#212529', headingFontSize: 32, headingFontStyle: 'normal', headingFontWeight: 600, headingLetterSpacing: -0.02, headingLineHeight: 1.25, headingTextDecoration: 'none', headingTextTransform: 'none', headings: { h1: { color: '#212529', fontSize: 42, fontWeight: '600', fontStyle: 'normal', letterSpacing: -0.02, lineHeight: 1.2, textDecoration: 'none', textTransform: 'none' }, h2: { color: '#212529', fontSize: 32, fontWeight: '600', fontStyle: 'normal', letterSpacing: -0.015, lineHeight: 1.25, textDecoration: 'none', textTransform: 'none' }, h3: { color: '#333333', fontSize: 24, fontWeight: '500', fontStyle: 'normal', letterSpacing: -0.01, lineHeight: 1.3, textDecoration: 'none', textTransform: 'none' }, h4: { color: '#333333', fontSize: 20, fontWeight: '500', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.35, textDecoration: 'none', textTransform: 'none' }, h5: { color: '#555555', fontSize: 16, fontWeight: '500', fontStyle: 'normal', letterSpacing: 0, lineHeight: 1.4, textDecoration: 'none', textTransform: 'none' }, h6: { color: '#007bff', fontSize: 14, fontWeight: '600', fontStyle: 'normal', letterSpacing: 0.02, lineHeight: 1.4, textDecoration: 'none', textTransform: 'uppercase' } }, lineHeight: 1.86, linkColor: '#007bff', linkHoverColor: '#0056b3', textColor: '#666666', primaryColor: '#007bff', primaryHoverColor: '#0056b3', accentColor: '#17a2b8', accentHoverColor: '#138496', sectionPaddingY: 64, sectionPaddingX: 24 },
      navigation: { backgroundColor: '#ffffff', textColor: '#333333', textHoverColor: '#007bff', activeColor: '#007bff', borderColor: '#e9ecef', borderEnabled: true, borderStyle: 'solid', borderWidth: 1, height: 72, logoPosition: 'left', marginLeft: 24, marginRight: 24, menuPosition: 'right', padding: 16, backdropBlur: true, dropdownBg: '#ffffff', dropdownTextColor: '#333333' },
      buttons: { primaryBg: '#007bff', primaryText: '#ffffff', primaryHover: '#0056b3', secondaryBg: '#f8f9fa', secondaryText: '#007bff', secondaryHover: '#e9ecef', borderRadius: 6 },
      cards: { backgroundColor: '#ffffff', borderRadius: 8, shadow: '0 2px 8px rgba(0,0,0,0.08)', hoverShadow: '0 8px 24px rgba(0,0,0,0.12)', borderColor: '#e9ecef', borderWidth: 1 },
      hero: { overlayColor: '#000000', overlayOpacity: 0.4, minHeight: '500px', contentAlign: 'center' },
      footer: { backgroundColor: '#212529', textColor: '#adb5bd', padding: 48 },
    },
  };

  getHeadingFormGroup(level: string): FormGroup {
    return this.globalThemingForm.get('headings')?.get(level) as FormGroup;
  }

  toggleHeading(level: string): void {
    this.expandedHeadings[level] = !this.expandedHeadings[level];
  }

  isHeadingExpanded(level: string): boolean {
    return this.expandedHeadings[level] || false;
  }

  private initForm(): void {
    this.settingsForm = this.fb.group({
      logo: [''],
      favicon: [''],
      languages: [[]],
      defaultLanguage: [''],
      seo: this.fb.group({
        headerTags: [''],
        bodyTags: [''],
        footerScripts: ['']
      })
    });

    this.themingForm = this.fb.group({
      menuPosition: ['left'],
      logoPosition: ['left'],
      backgroundColor: ['#1a1a1a'],
      textColor: ['#ffffff'],
      textHoverColor: ['rgba(255,255,255,0.8)'],
      activeColor: ['#ffffff'],
      borderEnabled: [true],
      borderColor: ['#000000'],
      borderWidth: [0],
      borderStyle: ['solid'],
      marginLeft: [0],
      marginRight: [0],
      padding: [16],
      height: [64],
      // CTA Button
      ctaEnabled: [false],
      ctaText: [''],
      ctaUrl: [''],
      ctaBgColor: [''],
      ctaTextColor: ['#ffffff'],
      ctaOpenInNewTab: [false]
    });

    this.topbarForm = this.fb.group({
      enabled: [false],
      backgroundColor: ['#111827'],
      textColor: ['#ffffff'],
      showAddress: [true],
      showPhone: [true],
      showEmail: [true],
      showSocials: [true],
    });

    this.footerForm = this.fb.group({
      companyName: [''],
      description: [''],
      address: [''],
      phone: [''],
      email: [''],
      footerLogo: [''],
      ctaText: [''],
      ctaUrl: [''],
      bottomText: [''],
      showSocials: [true],
      socials: this.fb.group({
        linkedin: [''],
        facebook: [''],
        instagram: [''],
        twitter: [''],
        youtube: [''],
      }),
    });

    this.globalThemingForm = this.fb.group({
      fontFamily: ['system'],
      backgroundColor: ['#ffffff'],
      textColor: ['#000000'],
      linkColor: ['#2563eb'],
      linkHoverColor: ['#1d4ed8'],
      bodyFontSize: [16],
      lineHeight: [1.5],
      headings: this.fb.group({
        h1: this.fb.group({
          fontSize: [48],
          fontWeight: ['700'],
          fontStyle: ['normal'],
          textTransform: ['none'],
          letterSpacing: [0],
          textDecoration: ['none'],
          lineHeight: [1.2],
          color: ['#111827']
        }),
        h2: this.fb.group({
          fontSize: [36],
          fontWeight: ['700'],
          fontStyle: ['normal'],
          textTransform: ['none'],
          letterSpacing: [0],
          textDecoration: ['none'],
          lineHeight: [1.2],
          color: ['#111827']
        }),
        h3: this.fb.group({
          fontSize: [30],
          fontWeight: ['600'],
          fontStyle: ['normal'],
          textTransform: ['none'],
          letterSpacing: [0],
          textDecoration: ['none'],
          lineHeight: [1.3],
          color: ['#111827']
        }),
        h4: this.fb.group({
          fontSize: [24],
          fontWeight: ['600'],
          fontStyle: ['normal'],
          textTransform: ['none'],
          letterSpacing: [0],
          textDecoration: ['none'],
          lineHeight: [1.3],
          color: ['#111827']
        }),
        h5: this.fb.group({
          fontSize: [20],
          fontWeight: ['600'],
          fontStyle: ['normal'],
          textTransform: ['none'],
          letterSpacing: [0],
          textDecoration: ['none'],
          lineHeight: [1.4],
          color: ['#111827']
        }),
        h6: this.fb.group({
          fontSize: [18],
          fontWeight: ['600'],
          fontStyle: ['normal'],
          textTransform: ['none'],
          letterSpacing: [0],
          textDecoration: ['none'],
          lineHeight: [1.4],
          color: ['#111827']
        })
      })
    });
  }

  private initNewLanguageForm(): void {
    this.newLanguageForm = this.fb.group({
      code: ['', [Validators.required, Validators.pattern('^[a-z]{2}$')]],
      name: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.currentDomain = this.websiteService.getCurrentDomain();
    if (!this.currentDomain) {
      this.router.navigate(['/']);
      return;
    }
    this.initCountries();
    this.loadData();
  }

  private initCountries(): void {
    // Load countries from JSON file
    this.http.get<{ code: string; name: string; languageCode: string; languageName: string }[]>('assets/countries.json')
      .subscribe({
        next: (countries) => {
          // Sort countries alphabetically by name
          this.allCountries = countries.sort((a, b) => a.name.localeCompare(b.name));
          this.filteredCountries = [...this.allCountries];
        },
        error: (error) => {
          console.error('Error loading countries:', error);
          this.notificationService.show({
            message: 'Error loading country list',
            type: 'error'
          });
          // Fallback to empty array
          this.allCountries = [];
          this.filteredCountries = [];
        }
      });
  }

  private async loadData(): Promise<void> {
    this.loading = true;
    try {
      await this.loadSettings();
      await this.loadWebsites();
    } catch (error) {
      this.errorMessage = 'Error loading data: ' + (error as Error).message;
    } finally {
      this.loading = false;
    }
  }

  private async loadSettings(): Promise<void> {
    if (!this.currentDomain) return;

    const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
    const settingsSnap = await getDoc(settingsRef);

    if (settingsSnap.exists()) {
      const settings = settingsSnap.data() as WebsiteSettings & { availableLanguages?: Language[] };
      
      // Load logo if it exists
      if (settings.logo) {
        this.settingsForm.patchValue({ logo: settings.logo });
      }

      // Load favicon if it exists
      if ((settings as any).favicon) {
        this.settingsForm.patchValue({ favicon: (settings as any).favicon });
      }
      
      // Load SEO settings if they exist
      if (settings.seo) {
        this.settingsForm.patchValue({
          seo: settings.seo
        });
      }
      
      // If we have availableLanguages, use those
      if (settings.availableLanguages) {
        this.availableLanguages = settings.availableLanguages;
        // Sort languages alphabetically by name
        this.availableLanguages.sort((a, b) => a.name.localeCompare(b.name));
        
        // Update the form with the selected language codes
        const selectedCodes = settings.availableLanguages.map(lang => lang.code);
        this.settingsForm.patchValue({
          languages: selectedCodes,
          defaultLanguage: settings.defaultLanguage
        });
      } else {
        // Handle legacy format (just language codes)
        const defaultLanguages = [
          { code: 'nl', name: 'Nederlands' },
          { code: 'en', name: 'English' },
          { code: 'de', name: 'Deutsch' },
          { code: 'fr', name: 'Français' },
          { code: 'es', name: 'Español' },
          { code: 'it', name: 'Italiano' },
          { code: 'pt', name: 'Português' }
        ];
        
        // If we have old format languages array, convert it
        if (settings.languages) {
          const selectedCodes = settings.languages;
          this.availableLanguages = defaultLanguages.filter(lang => 
            selectedCodes.includes(lang.code)
          );
          this.settingsForm.patchValue({
            languages: selectedCodes,
            defaultLanguage: settings.defaultLanguage
          });
        } else {
          // Initialize with default values
          this.availableLanguages = defaultLanguages;
          this.settingsForm.patchValue({
            languages: ['en'],
            defaultLanguage: 'en'
          });
        }
      }
      
      // Ensure logo is loaded even if not in the above branches
      if (settings.logo && !this.settingsForm.get('logo')?.value) {
        this.settingsForm.patchValue({ logo: settings.logo });
      }
      
      // Ensure SEO is loaded even if not in the above branches
      if (settings.seo && !this.settingsForm.get('seo')?.value) {
        this.settingsForm.patchValue({ seo: settings.seo });
      }

      // Load theme template selection
      const settingsAny = settings as any;
      if (settingsAny.themeTemplate) {
        this.selectedTemplate = settingsAny.themeTemplate;
      }

      // Load theming settings
      if (settings.theming?.navigation) {
        this.themingForm.patchValue(settings.theming.navigation);
      }
      if (settings.theming?.global) {
        const globalTheming = settings.theming.global;
        // Patch main form values
        this.globalThemingForm.patchValue({
          fontFamily: globalTheming.fontFamily,
          backgroundColor: globalTheming.backgroundColor,
          textColor: globalTheming.textColor,
          linkColor: globalTheming.linkColor,
          linkHoverColor: globalTheming.linkHoverColor,
          bodyFontSize: globalTheming.bodyFontSize,
          lineHeight: globalTheming.lineHeight
        });
        // Patch headings if they exist
        if (globalTheming.headings) {
          const headingsGroup = this.globalThemingForm.get('headings') as FormGroup;
          Object.keys(globalTheming.headings).forEach((key) => {
            const headingKey = key as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
            if (headingsGroup.get(headingKey)) {
              headingsGroup.get(headingKey)?.patchValue(globalTheming.headings[headingKey]);
            }
          });
        }
      }

      // Load topbar settings
      const topbar = (settings as any).topbar;
      if (topbar) {
        this.topbarForm.patchValue(topbar);
      }

      // Load footer/contact settings
      const footer = (settings as any).footer;
      if (footer) {
        this.footerForm.patchValue({
          companyName: footer.companyName || '',
          description: footer.description || '',
          address: footer.address || '',
          phone: footer.phone || '',
          email: footer.email || '',
          footerLogo: footer.footerLogo || '',
          ctaText: footer.ctaText || '',
          ctaUrl: footer.ctaUrl || '',
          bottomText: footer.bottomText || '',
          showSocials: footer.showSocials !== false,
        });
        if (footer.socials) {
          (this.footerForm.get('socials') as FormGroup).patchValue(footer.socials);
        }
        // Load footer columns
        if (footer.columns && Array.isArray(footer.columns)) {
          this.footerColumns = footer.columns.map((col: any) => ({
            title: col.title || '',
            links: (col.links || []).map((link: any) => ({ label: link.label || '', url: link.url || '' }))
          }));
        }
        // Load bottom links
        if (footer.bottomLinks && Array.isArray(footer.bottomLinks)) {
          this.footerBottomLinks = footer.bottomLinks.map((link: any) => ({ label: link.label || '', url: link.url || '' }));
        }
      }

      // Load banner settings for website and shop
      await this.loadBanners();
    } else {
      // Initialize with default values
      this.availableLanguages = [
        { code: 'nl', name: 'Nederlands' },
        { code: 'en', name: 'English' },
        { code: 'de', name: 'Deutsch' },
        { code: 'fr', name: 'Français' },
        { code: 'es', name: 'Español' },
        { code: 'it', name: 'Italiano' },
        { code: 'pt', name: 'Português' }
      ];
      this.settingsForm.patchValue({
        languages: ['en'],
        defaultLanguage: 'en',
        seo: {
          headerTags: '',
          bodyTags: '',
          footerScripts: ''
        }
      });
    }
  }

  private async loadBanners(): Promise<void> {
    if (!this.currentDomain) return;

    // Load website banner from website_settings
    try {
      const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
      const settingsSnap = await getDoc(settingsRef);
      if (settingsSnap.exists()) {
        const banner = settingsSnap.data()['banner'] || {};
        this.websiteBannerEnabled = banner.enabled || false;
        this.websiteBannerTextNl = banner.text_nl || '';
        this.websiteBannerTextEn = banner.text_en || '';
        this.websiteBannerTextDe = banner.text_de || '';
        this.websiteBannerTextFr = banner.text_fr || '';
        this.websiteBannerTextEs = banner.text_es || '';
        this.websiteBannerBgColor = banner.bgColor || '#1e40af';
        this.websiteBannerTextColor = banner.textColor || '#ffffff';
      }
    } catch (error) {
      console.error('Error loading website banner:', error);
    }

    // Load shop banner + shopDomain from shop_settings
    try {
      const shopRef = doc(this.firestore, 'shop_settings', this.currentDomain);
      const shopSnap = await getDoc(shopRef);
      if (shopSnap.exists()) {
        const data = shopSnap.data();
        const banner = data['banner'] || {};
        this.shopBannerEnabled = banner.enabled || false;
        this.shopBannerTextNl = banner.text_nl || '';
        this.shopBannerTextEn = banner.text_en || '';
        this.shopBannerTextDe = banner.text_de || '';
        this.shopBannerTextFr = banner.text_fr || '';
        this.shopBannerTextEs = banner.text_es || '';
        this.shopBannerBgColor = banner.bgColor || '#87bbc7';
        this.shopBannerTextColor = banner.textColor || '#ffffff';
      }
    } catch (error) {
      console.error('Error loading shop banner:', error);
    }
  }

  async saveWebsiteBanner(): Promise<void> {
    if (!this.currentDomain) return;
    try {
      const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
      await setDoc(settingsRef, {
        banner: {
          enabled: this.websiteBannerEnabled,
          text_nl: this.websiteBannerTextNl,
          text_en: this.websiteBannerTextEn,
          text_de: this.websiteBannerTextDe,
          text_fr: this.websiteBannerTextFr,
          text_es: this.websiteBannerTextEs,
          bgColor: this.websiteBannerBgColor,
          textColor: this.websiteBannerTextColor
        }
      }, { merge: true });
      this.notificationService.show({ message: 'Website melding opgeslagen', type: 'success' });
    } catch (error) {
      this.notificationService.show({ message: 'Fout bij opslaan: ' + (error as Error).message, type: 'error' });
    }
  }

  async saveShopBanner(): Promise<void> {
    if (!this.currentDomain) return;
    try {
      const shopRef = doc(this.firestore, 'shop_settings', this.currentDomain);
      await setDoc(shopRef, {
        banner: {
          enabled: this.shopBannerEnabled,
          text_nl: this.shopBannerTextNl,
          text_en: this.shopBannerTextEn,
          text_de: this.shopBannerTextDe,
          text_fr: this.shopBannerTextFr,
          text_es: this.shopBannerTextEs,
          bgColor: this.shopBannerBgColor,
          textColor: this.shopBannerTextColor
        }
      }, { merge: true });

      this.notificationService.show({ message: 'Shop melding opgeslagen', type: 'success' });
    } catch (error) {
      this.notificationService.show({ message: 'Fout bij opslaan: ' + (error as Error).message, type: 'error' });
    }
  }

  private loadWebsites(): void {
    this.websiteService.getWebsites().then(websites => {
      this.websites = websites;
    }).catch(error => {
      this.errorMessage = 'Error loading websites: ' + error.message;
    });
  }

  async onSaveSettings(): Promise<void> {
    if (!this.currentDomain || !this.settingsForm.valid) {
      this.errorMessage = !this.currentDomain ? 'No domain selected' : 'Please fill in all required fields';
      return;
    }

    try {
      const settings: WebsiteSettings = {
        domain: this.currentDomain,
        ...this.settingsForm.value
      };

      const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
      await setDoc(settingsRef, settings);

      this.errorMessage = '';
      // Show success message (you can use your notification service here)
    } catch (error) {
      this.errorMessage = 'Error saving settings: ' + (error as Error).message;
    }
  }


  onLanguageChange(event: Event): void {
    const checkbox = event.target as HTMLInputElement;
    const langCode = checkbox.value;
    const languages = new Set(this.settingsForm.get('languages')?.value || []);

    if (checkbox.checked) {
      languages.add(langCode);
    } else {
      languages.delete(langCode);
    }

    this.settingsForm.patchValue({ languages: Array.from(languages) });
  }

  isLanguageSelected(langCode: string): boolean {
    const languages = this.settingsForm.get('languages')?.value || [];
    return languages.includes(langCode);
  }

  getLanguageName(code: string): string {
    const language = this.availableLanguages.find(l => l.code === code);
    return language?.name || code.toUpperCase();
  }

  async onSaveLanguageSettings(): Promise<void> {
    if (!this.currentDomain) return;

    try {
      // Get the selected language codes
      const selectedCodes = this.settingsForm.get('languages')?.value || [];
      
      // Convert selected codes to full language objects
      const selectedLanguages = this.availableLanguages.filter(lang => 
        selectedCodes.includes(lang.code)
      );

      // Update availableLanguages to only include selected languages
      this.availableLanguages = selectedLanguages;

      const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
      await setDoc(settingsRef, {
        availableLanguages: selectedLanguages,
        defaultLanguage: this.settingsForm.get('defaultLanguage')?.value
      }, { merge: true });
      
      this.notificationService.show({
        message: 'Language settings saved successfully',
        type: 'success'
      });
    } catch (error) {
      this.notificationService.show({
        message: 'Error saving language settings: ' + (error as Error).message,
        type: 'error'
      });
    }
  }

  async onSaveSeoSettings(): Promise<void> {
    if (!this.currentDomain) return;

    try {
      const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
      await setDoc(settingsRef, {
        seo: this.settingsForm.get('seo')?.value
      }, { merge: true });
      
      this.notificationService.show({
        message: 'SEO settings saved successfully',
        type: 'success'
      });
    } catch (error) {
      this.notificationService.show({
        message: 'Error saving SEO settings: ' + (error as Error).message,
        type: 'error'
      });
    }
  }

  async onLogoUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length || !this.currentDomain) return;

    const file = input.files[0];
    
    if (!file.type.startsWith('image/')) {
      this.notificationService.show({
        message: 'Please upload an image file',
        type: 'error'
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      this.notificationService.show({
        message: 'File size should be less than 2MB',
        type: 'error'
      });
      return;
    }

    try {
      this.logoUploading = true;

      const storagePath = `logos/${this.currentDomain}/${Date.now()}-logo.webp`;
      const downloadURL = await this.imageUploadService.compressAndUpload(file, storagePath, 800);
      
      const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
      await setDoc(settingsRef, { logo: downloadURL }, { merge: true });
      
      this.settingsForm.patchValue({ logo: downloadURL });
      
      this.notificationService.show({
        message: 'Logo uploaded successfully',
        type: 'success'
      });
    } catch (error) {
      this.notificationService.show({
        message: 'Error uploading logo: ' + (error as Error).message,
        type: 'error'
      });
    } finally {
      this.logoUploading = false;
    }
  }

  async onDeleteLogo(): Promise<void> {
    if (!this.currentDomain || !this.settingsForm.get('logo')?.value) return;

    try {
      // Get the current logo URL
      const logoUrl = this.settingsForm.get('logo')?.value;
      
      // Create a reference from the URL
      const logoRef = ref(this.storage, logoUrl);
      
      // Delete the file from storage
      await deleteObject(logoRef);
      
      // Update Firestore to remove the logo URL
      const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
      await setDoc(settingsRef, { logo: null }, { merge: true });
      
      // Update form
      this.settingsForm.patchValue({ logo: '' });
      
      this.notificationService.show({
        message: 'Logo deleted successfully',
        type: 'success'
      });
    } catch (error) {
      this.notificationService.show({
        message: 'Error deleting logo: ' + (error as Error).message,
        type: 'error'
      });
    }
  }

  async onFaviconUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length || !this.currentDomain) return;

    const file = input.files[0];

    if (!file.type.startsWith('image/')) {
      this.notificationService.show({
        message: 'Please upload an image file',
        type: 'error'
      });
      return;
    }

    if (file.size > 1 * 1024 * 1024) {
      this.notificationService.show({
        message: 'Favicon should be less than 1MB',
        type: 'error'
      });
      return;
    }

    try {
      this.faviconUploading = true;

      const storagePath = `favicons/${this.currentDomain}/${Date.now()}-favicon.webp`;
      const downloadURL = await this.imageUploadService.compressAndUpload(file, storagePath, 256);

      const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
      await setDoc(settingsRef, { favicon: downloadURL }, { merge: true });

      this.settingsForm.patchValue({ favicon: downloadURL });

      this.notificationService.show({
        message: 'Favicon uploaded successfully',
        type: 'success'
      });
    } catch (error) {
      this.notificationService.show({
        message: 'Error uploading favicon: ' + (error as Error).message,
        type: 'error'
      });
    } finally {
      this.faviconUploading = false;
    }
  }

  async onDeleteFavicon(): Promise<void> {
    if (!this.currentDomain || !this.settingsForm.get('favicon')?.value) return;

    try {
      const faviconUrl = this.settingsForm.get('favicon')?.value;
      const faviconRef = ref(this.storage, faviconUrl);

      await deleteObject(faviconRef);

      const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
      await setDoc(settingsRef, { favicon: null }, { merge: true });

      this.settingsForm.patchValue({ favicon: '' });

      this.notificationService.show({
        message: 'Favicon deleted successfully',
        type: 'success'
      });
    } catch (error) {
      this.notificationService.show({
        message: 'Error deleting favicon: ' + (error as Error).message,
        type: 'error'
      });
    }
  }

  toggleAddLanguageForm(): void {
    this.showAddLanguageForm = !this.showAddLanguageForm;
    if (this.showAddLanguageForm) {
      this.newLanguageForm.reset();
    }
  }

  openAddLanguageModal(): void {
    this.showAddLanguageModal = true;
    this.countrySearchTerm = '';
    this.filteredCountries = [...this.allCountries];
  }

  closeAddLanguageModal(): void {
    this.showAddLanguageModal = false;
    this.countrySearchTerm = '';
    this.filteredCountries = [...this.allCountries];
  }

  filterCountries(): void {
    const searchTerm = this.countrySearchTerm.toLowerCase().trim();
    if (!searchTerm) {
      this.filteredCountries = [...this.allCountries];
    } else {
      this.filteredCountries = this.allCountries.filter(country =>
        country.name.toLowerCase().includes(searchTerm) ||
        country.languageName.toLowerCase().includes(searchTerm) ||
        country.code.toLowerCase().includes(searchTerm)
      );
    }
  }

  async selectCountry(country: { code: string; name: string; languageCode: string; languageName: string }): Promise<void> {
    const newLanguage: Language = {
      code: country.languageCode,
      name: country.languageName
    };

    // Check if language code already exists
    if (this.availableLanguages.some(lang => lang.code === newLanguage.code)) {
      this.notificationService.show({
        message: `Language '${newLanguage.name}' (${newLanguage.code.toUpperCase()}) already exists`,
        type: 'error'
      });
      return;
    }

    if (!this.currentDomain) return;

    try {
      // Add to available languages locally
      this.availableLanguages.push(newLanguage);
      
      // Sort languages alphabetically by name
      this.availableLanguages.sort((a, b) => a.name.localeCompare(b.name));

      // Save to Firestore
      const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
      await setDoc(settingsRef, {
        availableLanguages: this.availableLanguages
      }, { merge: true });

      // Add the new language to the selected languages in the form
      const currentLanguages = new Set(this.settingsForm.get('languages')?.value || []);
      currentLanguages.add(newLanguage.code);
      this.settingsForm.patchValue({ languages: Array.from(currentLanguages) });

      // Close modal and show success message
      this.closeAddLanguageModal();
      this.notificationService.show({
        message: `Language '${newLanguage.name}' added successfully`,
        type: 'success'
      });
    } catch (error) {
      // Remove from local state if Firestore save fails
      this.availableLanguages = this.availableLanguages.filter(
        lang => lang.code !== newLanguage.code
      );
      
      this.notificationService.show({
        message: 'Error saving language: ' + (error as Error).message,
        type: 'error'
      });
    }
  }

  async addNewLanguage(): Promise<void> {
    if (this.newLanguageForm.valid && this.currentDomain) {
      const newLanguage: Language = this.newLanguageForm.value;
      
      // Check if language code already exists
      if (this.availableLanguages.some(lang => lang.code === newLanguage.code)) {
        this.notificationService.show({
          message: `Language code '${newLanguage.code}' already exists`,
          type: 'error'
        });
        return;
      }

      try {
        // Add to available languages locally
        this.availableLanguages.push(newLanguage);
        
        // Sort languages alphabetically by name
        this.availableLanguages.sort((a, b) => a.name.localeCompare(b.name));

        // Save to Firestore
        const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
        await setDoc(settingsRef, {
          availableLanguages: this.availableLanguages
        }, { merge: true });

        // Close form and show success message
        this.toggleAddLanguageForm();
        this.notificationService.show({
          message: 'Language added successfully',
          type: 'success'
        });
      } catch (error) {
        // Remove from local state if Firestore save fails
        this.availableLanguages = this.availableLanguages.filter(
          lang => lang.code !== newLanguage.code
        );
        
        this.notificationService.show({
          message: 'Error saving language: ' + (error as Error).message,
          type: 'error'
        });
      }
    }
  }

  onSelectTemplate(templateId: string): void {
    const template = this.templateData[templateId];
    if (!template) return;

    this.selectedTemplate = templateId;

    // Patch navigation form
    this.themingForm.patchValue({
      menuPosition: template.navigation.menuPosition,
      logoPosition: template.navigation.logoPosition,
      backgroundColor: template.navigation.backgroundColor,
      textColor: template.navigation.textColor || '#ffffff',
      textHoverColor: template.navigation.textHoverColor || 'rgba(255,255,255,0.8)',
      activeColor: template.navigation.activeColor || '#ffffff',
      borderEnabled: template.navigation.borderEnabled,
      borderColor: template.navigation.borderColor,
      borderWidth: template.navigation.borderWidth,
      borderStyle: template.navigation.borderStyle,
      marginLeft: template.navigation.marginLeft,
      marginRight: template.navigation.marginRight,
      padding: template.navigation.padding,
      height: template.navigation.height,
    });

    // Patch global form
    this.globalThemingForm.patchValue({
      fontFamily: template.global.fontFamily,
      backgroundColor: template.global.backgroundColor,
      textColor: template.global.textColor,
      linkColor: template.global.linkColor,
      linkHoverColor: template.global.linkHoverColor,
      bodyFontSize: template.global.bodyFontSize,
      lineHeight: template.global.lineHeight,
    });

    // Patch headings
    const headingsGroup = this.globalThemingForm.get('headings') as FormGroup;
    if (template.global.headings) {
      Object.keys(template.global.headings).forEach((key) => {
        const headingKey = key as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
        if (headingsGroup.get(headingKey) && template.global.headings[headingKey]) {
          headingsGroup.get(headingKey)?.patchValue(template.global.headings[headingKey]!);
        }
      });
    }
  }

  async onSaveThemingSettings(): Promise<void> {
    if (!this.currentDomain) return;

    try {
      const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
      await setDoc(settingsRef, {
        themeTemplate: this.selectedTemplate || null,
        theming: {
          navigation: this.themingForm.value,
          global: this.globalThemingForm.value
        }
      }, { merge: true });

      this.notificationService.show({
        message: 'Theming settings saved successfully',
        type: 'success'
      });
    } catch (error) {
      this.notificationService.show({
        message: 'Error saving theming settings: ' + (error as Error).message,
        type: 'error'
      });
    }
  }

  async onSaveTopbarSettings(): Promise<void> {
    if (!this.currentDomain) return;

    try {
      const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
      await setDoc(settingsRef, {
        topbar: this.topbarForm.value
      }, { merge: true });

      this.notificationService.show({
        message: 'Topbar instellingen opgeslagen',
        type: 'success'
      });
    } catch (error) {
      this.notificationService.show({
        message: 'Fout bij opslaan topbar: ' + (error as Error).message,
        type: 'error'
      });
    }
  }

  async onFooterLogoUpload(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length || !this.currentDomain) return;

    const file = input.files[0];

    if (!file.type.startsWith('image/')) {
      this.notificationService.show({ message: 'Upload een afbeelding', type: 'error' });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      this.notificationService.show({ message: 'Bestand moet kleiner zijn dan 2MB', type: 'error' });
      return;
    }

    try {
      this.footerLogoUploading = true;
      const storagePath = `logos/${this.currentDomain}/${Date.now()}-footer-logo.webp`;
      const downloadURL = await this.imageUploadService.compressAndUpload(file, storagePath, 800);

      this.footerForm.patchValue({ footerLogo: downloadURL });

      this.notificationService.show({ message: 'Footer logo geüpload', type: 'success' });
    } catch (error) {
      this.notificationService.show({ message: 'Fout bij uploaden: ' + (error as Error).message, type: 'error' });
    } finally {
      this.footerLogoUploading = false;
    }
  }

  onDeleteFooterLogo(): void {
    this.footerForm.patchValue({ footerLogo: '' });
    this.notificationService.show({ message: 'Footer logo verwijderd. Sla op om te bevestigen.', type: 'success' });
  }

  async onSaveFooterSettings(): Promise<void> {
    if (!this.currentDomain) return;

    try {
      const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);
      const footerData = {
        ...this.footerForm.value,
        columns: this.footerColumns,
        bottomLinks: this.footerBottomLinks,
      };
      await setDoc(settingsRef, {
        footer: footerData
      }, { merge: true });

      this.notificationService.show({
        message: 'Contact & footer instellingen opgeslagen',
        type: 'success'
      });
    } catch (error) {
      this.notificationService.show({
        message: 'Fout bij opslaan: ' + (error as Error).message,
        type: 'error'
      });
    }
  }

  // Footer column management
  addFooterColumn(): void {
    if (this.footerColumns.length >= 3) return;
    this.footerColumns.push({ title: '', links: [] });
  }

  removeFooterColumn(index: number): void {
    this.footerColumns.splice(index, 1);
  }

  addColumnLink(colIndex: number): void {
    this.footerColumns[colIndex].links.push({ label: '', url: '' });
  }

  removeColumnLink(colIndex: number, linkIndex: number): void {
    this.footerColumns[colIndex].links.splice(linkIndex, 1);
  }

  // Bottom links management
  addBottomLink(): void {
    this.footerBottomLinks.push({ label: '', url: '' });
  }

  removeBottomLink(index: number): void {
    this.footerBottomLinks.splice(index, 1);
  }

  async saveAllSettings(): Promise<void> {
    if (!this.currentDomain) return;

    this.savingAll = true;
    const errors: string[] = [];

    try {
      const settingsRef = doc(this.firestore, 'website_settings', this.currentDomain);

      // Build the complete settings object
      const selectedCodes = this.settingsForm.get('languages')?.value || [];
      const selectedLanguages = this.availableLanguages.filter(lang =>
        selectedCodes.includes(lang.code)
      );

      const footerData = {
        ...this.footerForm.value,
        columns: this.footerColumns,
        bottomLinks: this.footerBottomLinks,
      };

      const allData: Record<string, any> = {
        // Language settings
        availableLanguages: selectedLanguages,
        defaultLanguage: this.settingsForm.get('defaultLanguage')?.value,
        // SEO
        seo: this.settingsForm.get('seo')?.value,
        // Theming (navigation + global)
        themeTemplate: this.selectedTemplate || null,
        theming: {
          navigation: this.themingForm.value,
          global: this.globalThemingForm.value,
        },
        // Footer
        footer: footerData,
        // Topbar
        topbar: this.topbarForm.value,
      };

      // Include logo if set
      const logo = this.settingsForm.get('logo')?.value;
      if (logo) {
        allData['logo'] = logo;
      }

      await setDoc(settingsRef, allData, { merge: true });

      this.notificationService.show({
        message: 'Alle instellingen opgeslagen',
        type: 'success'
      });
    } catch (error) {
      this.notificationService.show({
        message: 'Fout bij opslaan: ' + (error as Error).message,
        type: 'error'
      });
    } finally {
      this.savingAll = false;
    }
  }

} 